import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
} from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import Header166 from "../../header/header";
import "bootstrap/dist/css/bootstrap.min.css";
import { useSelector } from "react-redux";
import { selectAppPermissions } from "../../../store/auth/selectors";
import {
  getBodegas,
  createBodega,
  updateBodega,
  deleteBodega,
  getResponsables,
} from "../../../services/financiero/bodegas/getBodegas";
import getDeptosMunicipios from "../../../services/departMunic/deptosMunicipiosServices";
import "./bodegas.css";

const { Option } = Select;

const Bodegas = () => {
  const [bodegas, setBodegas] = useState([]);
  const [deptosMunicipios, setDeptosMunicipios] = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [munis, setMunis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentBodega, setCurrentBodega] = useState(null);
  const [form] = Form.useForm();

  const [searchNombre, setSearchNombre] = useState("");
  const [filtroDepto, setFiltroDepto] = useState(null);
  const [filtroMunicipio, setFiltroMunicipio] = useState(null);
  const [filtroResponsable, setFiltroResponsable] = useState(null);

  //Control de Permisos
  const appName = "bodegas";
  const permissions = useSelector((state) =>
    selectAppPermissions(state, appName)
  );
  const permissionDelete = permissions.priv_delete === "Y";
  const permissionUpdate = permissions.priv_update === "Y";
  const permissionInsert = permissions.priv_insert === "Y";

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // 🚚 traer todas las bodegas
  const fetchBodegas = async () => {
    setLoading(true);
    try {
      const resp = await getBodegas(); // { status: number, bodegas: Bodega[] }
      if (resp.status === 200 && Array.isArray(resp.bodegas)) {
        setBodegas(resp.bodegas);
      } else {
        message.error(resp.message || "Error cargando bodegas");
      }
    } catch {
      message.error("Error cargando bodegas");
    } finally {
      setLoading(false);
    }
  };

  // 🚚 traer departamentos + municipios
  const fetchDeptosMunicipios = async () => {
    try {
      const resp = await getDeptosMunicipios(); // { status, data: [...] }

      if (resp.status === 200 && Array.isArray(resp.data)) {
        setDeptosMunicipios(resp.data);
      } else {
        setDeptosMunicipios([]);
        message.error(
          resp.message || "Error al cargar departamentos y municipios"
        );
      }
    } catch {
      message.error("Error cargando departamentos y municipios");
    }
  };

  const fetchResponsables = async () => {
    try {
      const resp = await getResponsables(); // { status, data: [...] }

      if (resp.status === 200 && Array.isArray(resp.data)) {
        setResponsables(resp.data);
      } else {
        message.error(resp.message || "Error al cargar responsables");
        setResponsables([]);
      }
    } catch (error) {
      console.error("Error al obtener responsables:", error);
      message.error("Error al cargar responsables");
      setResponsables([]);
    }
  };

  useEffect(() => {
    fetchBodegas();
    fetchDeptosMunicipios();
    fetchResponsables();
  }, []);

  const bodegasFiltradas = useMemo(() => {
    return bodegas.filter((b) => {
      const matchNombre = b.nombre_bodega
        .toLowerCase()
        .includes(searchNombre.toLowerCase());
      const matchDepto = filtroDepto
        ? b.departamento_oid === filtroDepto
        : true;
      const matchMuni = filtroMunicipio
        ? b.municipio_oid === filtroMunicipio
        : true;
      const matchResp = filtroResponsable
        ? b.responsable_login === filtroResponsable
        : true;
      return matchNombre && matchDepto && matchMuni && matchResp;
    });
  }, [bodegas, searchNombre, filtroDepto, filtroMunicipio, filtroResponsable]);

  // cuando cambia departamento, llenamos municipios
  const handleDeptoChange = (deptoOid) => {
    const dept = deptosMunicipios.find((d) => d.oid_departamento === deptoOid);
    setMunis(dept?.municipios || []);
    // limpiar selector municipio previo
    form.setFieldsValue({ oid_municipio: undefined });
  };

  // 📤 crear / actualizar
  // 📤 crear / actualizar
  const handleSubmit = async (values) => {
    try {
      let resp;
      if (isEditing) {
        resp = await updateBodega(currentBodega.bodega_oid, values);
      } else {
        // createBodega debe devolver { status, message }
        resp = await createBodega(values);
      }

      // recogemos el status y el message que envía el backend
      if (resp.status === 200 || resp.status === 201) {
        message.success(resp.message);
        setIsModalOpen(false);
        form.resetFields();
        await fetchBodegas();
      } else {
        message.error("Ocurrió un problema al guardar la bodega.");
      }
    } catch (error) {
      message.error("Ocurrió un problema al guardar la bodega.");
    }
  };

  // 🗑️ eliminar
  const handleDelete = async (oid) => {
    try {
      // deleteBodega debe devolver { status, message }
      const resp = await deleteBodega(oid);

      if (resp.status === 200) {
        message.success(resp.message);
        fetchBodegas();
      } else {
        message.error("No se pudo eliminar la bodega.");
      }
    } catch (error) {
      message.error("No se pudo eliminar la bodega.");
    }
  };

  // abrir modal editar
  // abrir modal editar
  const openEditModal = (record) => {
    setIsEditing(true);
    setCurrentBodega(record);

    // limpia y carga el select de departamentos y municipios
    form.resetFields();
    handleDeptoChange(record.departamento_oid);

    // ahora que 'munis' ya está cargado, setea todos los campos de golpe
    form.setFieldsValue({
      nombre_bodega: record.nombre_bodega,
      oid_departamento: record.departamento_oid,
      oid_municipio: record.municipio_oid,
      responsable_login: record.responsable_login, // <-- aquí el cambio
    });

    setIsModalOpen(true);
  };

  // abrir modal crear
  const openCreateModal = () => {
    setIsEditing(false);
    setCurrentBodega(null);
    form.resetFields();
    setMunis([]);
    setIsModalOpen(true);
  };

  const columns = [
    {
      title: "Acciones",
      key: "actions",
      render: (_, record) => (
        <div className="center-actions">
          {permissionUpdate && (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          )}
          {permissionDelete && (
            <Popconfirm
              title="¿Eliminar esta bodega?"
              onConfirm={() => handleDelete(record.bodega_oid)}
              okText="Sí"
              cancelText="No"
            >
              <Button type="primary" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </div>
      ),
    },
    {
      title: "ID",
      dataIndex: "bodega_oid",
      key: "bodega_oid",
      align: "center",
    },
    { title: "Nombre", dataIndex: "nombre_bodega", key: "nombre_bodega" },
    { title: "Municipio", dataIndex: "municipio", key: "municipio" },
    { title: "Departamento", dataIndex: "departamento", key: "departamento" },
    {
      title: "Responsable",
      dataIndex: "responsable_nombre",
      key: "responsable_nombre",
      align: "center",
    },
  ];

  return (
    <div className="container">
      <Header166 type="g" />

      <div className="d-flex flex-wrap gap-3 mb-3 justify-content-between">
        <div className="d-flex gap-2">
          <Input
            placeholder="Buscar por nombre"
            value={searchNombre}
            onChange={(e) => setSearchNombre(e.target.value)}
            style={{ width: 200 }}
          />
          <Select
            showSearch
            allowClear
            placeholder="Filtrar por departamento"
            value={filtroDepto}
            onChange={(val) => {
              setFiltroDepto(val);
              setFiltroMunicipio(null);
            }}
            style={{ width: 220 }}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.children?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {deptosMunicipios.map((d) => (
              <Option key={d.oid_departamento} value={d.oid_departamento}>
                {d.nombre_departamento}
              </Option>
            ))}
          </Select>

          <Select
            showSearch
            allowClear
            placeholder="Filtrar por municipio"
            value={filtroMunicipio}
            onChange={setFiltroMunicipio}
            style={{ width: 220 }}
            disabled={!filtroDepto}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.children?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {(
              deptosMunicipios.find((d) => d.oid_departamento === filtroDepto)
                ?.municipios || []
            ).map((m) => (
              <Option key={m.oid_municipio} value={m.oid_municipio}>
                {m.nombre_municipio}
              </Option>
            ))}
          </Select>

          <Select
            showSearch
            allowClear
            placeholder="Filtrar por responsable"
            value={filtroResponsable}
            onChange={setFiltroResponsable}
            style={{ width: 220 }}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {responsables.map((r) => (
              <Option key={r.login} value={r.login}>
                {r.name}
              </Option>
            ))}
          </Select>
          <Button
            onClick={() => {
              setSearchNombre("");
              setFiltroDepto(null);
              setFiltroMunicipio(null);
              setFiltroResponsable(null);
            }}
          >
            Limpiar
          </Button>
        </div>
        <div>
          {permissionInsert && (
            <Button type="primary" onClick={openCreateModal}>
              Nueva Bodega
            </Button>
          )}
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={bodegasFiltradas}
        rowKey="bodega_oid"
        loading={loading}
        bordered
        size="small"
        scroll={{ y: 500, x: "max-content" }}
        pagination={{
          current: currentPage, // página actual
          pageSize: pageSize, // filas por página
          total: bodegasFiltradas.length, // total de registros
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} registros`,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
      />

      <Modal
        title={isEditing ? "Editar Bodega" : "Nueva Bodega"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="nombre_bodega"
            label="Nombre"
            rules={[{ required: true, message: "El nombre es obligatorio" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="oid_departamento"
            label="Departamento"
            rules={[{ required: true, message: "Debe elegir un departamento" }]}
          >
            <Select
              placeholder="Seleccione departamento"
              onChange={handleDeptoChange}
              allowClear
            >
              {deptosMunicipios.map((d) => (
                <Option key={d.oid_departamento} value={d.oid_departamento}>
                  {d.nombre_departamento}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="oid_municipio"
            label="Municipio"
            rules={[{ required: true, message: "Debe elegir un municipio" }]}
          >
            <Select
              placeholder="Seleccione municipio"
              disabled={!munis.length}
              allowClear
            >
              {munis.map((m) => (
                <Option key={m.oid_municipio} value={m.oid_municipio}>
                  {m.nombre_municipio}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="responsable_login"
            label="Responsable"
            rules={[
              { required: true, message: "El responsable es obligatorio" },
            ]}
          >
            <Select
              showSearch
              placeholder="Selecciona un responsable"
              optionFilterProp="children"
              filterOption={(input, option) =>
                // filtra por el texto que ve el usuario
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {responsables.map((r) => (
                <Option key={r.login} value={r.login}>
                  {r.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <div className="d-flex justify-content-end gap-2">
            <Button onClick={() => setIsModalOpen(false)}>Cancelar</Button>
            <Button type="primary" htmlType="submit">
              {isEditing ? "Actualizar" : "Crear"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Bodegas;
